import { useMemo, useState } from "react";
import WithAuth from "../../hoc/WithAuth";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import { useGetInputPrice } from "../../api/admin";
import InputPriceModal from "../../components/modals/InputPriceModal";
import { DataTable } from "../../components/ui/data-table";
import { InputPrice } from "../../types/input-price";
import { Icons } from "../../assets/images/icons";
import { useTablePrint } from "../../hooks/useTablePrint";
import { formatDateToISO } from "../../util/date-formatter";
import { Button } from "../../components/ui/button";
import { ExportButton } from "../../components/ui/ExportButton";
import { AppButton } from "../../components/ui/AppButton";
import { Input } from "../../components/ui/input";
import { StatusBadge } from "../../components/shared/status-badge";

function InputPricePage() {
  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  /** API */
  const { data: inputPrices, isLoading } = useGetInputPrice({
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });
  /** Table Print */
  const { handlePrint } = useTablePrint();

  /** Transform orders for table */
  const transformedInputPrices = useMemo((): InputPrice[] => {
    if (!inputPrices?.data?.content) return [];
    return inputPrices.data.content.map((item: InputPrice) => ({
      id: item.id,
      inputName: item.input?.inputName || "N/A",
      type: item.type,
      organization: item?.organization?.orgName || "N/A",
      inputPrice: item.inputPrice,
      effectiveDate: item.effectiveDate,
      status: item.status, // for display only
      archived: item.archived,
    }));
  }, [inputPrices]);

  const [selectedId, setSelectedId] = useState<number | undefined>();
  const [open, setOpen] = useState(false);
  const { user } = useAuth() as AuthContextValue;

  /** Columns */
  const inputPriceColumns = [
    {
      key: "inputName",
      title: "Input",
      dataIndex: "inputName",
      align: "left" as const,
    },
    {
      key: "type",
      title: "Type",
      dataIndex: "type",
      align: "left" as const,
    },
    {
      key: "organization",
      title: "Organization",
      dataIndex: "organization",
      align: "left" as const,
    },
    {
      key: "inputPrice",
      title: "Input Price (ETB)",
      dataIndex: "inputPrice",
      align: "right" as const,
      render: (v: number) => v?.toLocaleString(),
    },
    {
      key: "effectiveDate",
      title: "Effective Date",
      dataIndex: "effectiveDate",
      align: "left" as const,
      render: (v: string) => formatDateToISO(v),
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      align: "left" as const,
      render: (value: string) => <StatusBadge value={value} />,
    },
    {
      key: "archived",
      title: "Archived",
      dataIndex: "archived",
      align: "left" as const,
      render: (archived: boolean) => {
        const isArchived = Boolean(archived);

        return (
          <span
            className={`inline-flex min-w-[70px] justify-center rounded-md px-3 py-1 text-xs font-semibold
          ${
            isArchived
              ? "bg-red-100 text-red-600"
              : "bg-green-100 text-green-600"
          }
        `}
          >
            {isArchived ? "Yes" : "No"}
          </span>
        );
      },
    },
  ];

  const dangerColumns = [
    {
      key: "actions",
      dataIndex: "id",
      title: "Actions",
      render: (_: any, record: any) => (
        <Button
          variant="default"
          disabled={record.status === "INACTIVE" || record.archived === true}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedId(record.id);
            setOpen(true);
          }}
        >
          <img
            src={Icons.pencilEdit}
            alt="edit"
            className="h-4 w-4 text-gray-100 mr-1"
          />
          Edit
        </Button>
      ),
    },
  ];

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleCreate = () => setOpen(true);

  const hideModal = () => {
    setSelectedId(undefined);
    setOpen(false);
  };

  return (
    <>
      <div className="hidden md:block rounded-xl bg-white">
        <div className="flex flex-col sm:flex-row justify-between">
          <div className="relative w-full lg:w-64">
            <Input
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search here by input name"
              className="pl-10 rounded-2xl w-[308px] h-11"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <img src={Icons.search} alt="search" className="w-6 h-6" />
            </div>
          </div>

          <div className="flex gap-2">
            <AppButton
              customIcon={Icons.printIcon}
              variant="outline"
              onClick={() =>
                handlePrint(
                  inputPriceColumns,
                  transformedInputPrices,
                  "Input Price Report",
                )
              }
              className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
            >
              Print
            </AppButton>

            <ExportButton
              columns={inputPriceColumns}
              data={transformedInputPrices}
              fileName="input_prices"
              title="Input Price Report"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: inputPrices?.data?.totalElements ?? 0,
              }}
              customIcon={Icons.fileExport}
              className="bg-[#DDDDDD] hover:bg-gray-300"
            />

            {user?.roleName === "ROLE_KFT_SUPER_ADMIN" && (
              <AppButton
                customIcon={Icons.addCircle}
                variant="success"
                onClick={handleCreate}
              >
                Create New
              </AppButton>
            )}
          </div>
        </div>

        {/* Desktop View */}
        <DataTable<InputPrice>
          data={transformedInputPrices}
          columns={
            user?.roleName === "ROLE_KFT_SUPER_ADMIN"
              ? [...inputPriceColumns, ...dangerColumns]
              : inputPriceColumns
          }
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: inputPrices?.data?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No input price found"
          rowKey="id"
        />
      </div>

      {/* Create modal */}
      <InputPriceModal
        open={open}
        selectedId={selectedId}
        hideModal={hideModal}
      />
    </>
  );
}

export default WithAuth(InputPricePage);
